<script setup lang="js">
import QuestionService from "@/services/question.service.js";
import ErrorUtils from "@/utils/error.utils.js";
import StringUtils from '@/utils/string.utils.js';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useConfirm } from "primevue/useconfirm";
import { nextTick, ref, watch } from "vue";

const confirm = useConfirm();
const props = defineProps([
  'item',
  'hideItemVisualization',
  'itemVisualizationDialogVisible'
]);
const emit = defineEmits(['updateStoredItem']);
const feedback = ref(null);
const exporting = ref(false);
const feedbackOptions = [
  {label: 'Muito bom', value: 5, icon: 'pi pi-thumbs-up'},
  {label: 'Bom', value: 4, icon: 'pi pi-thumbs-up'},
  {label: 'Neutro', value: 3, icon: 'pi pi-minus'},
  {label: 'Ruim', value: 2, icon: 'pi pi-thumbs-down'},
  {label: 'Muito ruim', value: 1, icon: 'pi pi-thumbs-down'}
];

const bloomTaxonomies = [
  {value: 'remember', label: 'Lembrar'},
  {value: 'understand', label: 'Entender'},
  {value: 'apply', label: 'Aplicar'},
  {value: 'analyze', label: 'Analisar'},
  {value: 'evaluate', label: 'Avaliar'},
  {value: 'create', label: 'Criar'}
];

const PX_TO_MM = 25.4 / 96;
const PDF_MARGIN = 15;
const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text || '');
};

const getCorrectAlternative = () => {
  const correctAnswer = props.item?.question?.correctAnswer;
  return props.item?.question?.alternatives?.find(a => a.number === correctAnswer)?.number;
};

const correctAlternative = ref(null);
const onCloseFeedback = (event) => {
  if (hasFeedback() || event.relatedTarget?.nodeName === "BUTTON") return;
  feedback.value = null;
};

const sendFeedback = (event, feedbackValue) => {
  if (hasFeedback()) return;

  const f = feedbackOptions.find(option => option.value === feedbackValue);
  if (!f) return;

  confirm.require({
    target: event.target,
    message: 'Confirmar o feedback?',
    icon: 'pi ' + f.icon,
    rejectProps: { label: 'Cancelar', severity: 'secondary', outlined: true },
    acceptProps: { label: 'Confirmar' },
    acceptIcon: 'pi pi-check',
    accept: () => {
      QuestionService.saveFeedback(
        props.item.question?.id,
        props.item.question?.version,
        f.label
      )
      .then(() => {
        try {
            props.item.question.feedback = f.label;
            QuestionService.updateQuestion(props.item);
            emit('updateStoredItem', props.item);
        } catch (e) {
          ErrorUtils.handleError(e);
        }
      })
      .catch(e => ErrorUtils.handleError(e));
    },
    reject: () => {
      feedback.value = null;
    },
    onHide: () => {
      feedback.value = null;
    }
  });
};

const hasFeedback = () => {
  return props.item?.question.feedback !== undefined &&
         props.item?.question.feedback !== null;
};

function sanitizeContentNode(originalRoot) {
  const clone = originalRoot.cloneNode(true);

  const feedbackFieldset = clone.querySelector('#feedbackFieldset');
  if (feedbackFieldset) feedbackFieldset.remove();

  clone.querySelectorAll('.pi-copy').forEach(btn => {
    const button = btn.closest('button');
    if (button) button.remove();
    else btn.remove();
  });

  clone.querySelectorAll('canvas').forEach(canvas => {
    console.log('🔍 DEBUG: Encontrado canvas, removendo para evitar erro', {
      width: canvas.width,
      height: canvas.height
    });
    canvas.remove();
  });

  clone.querySelectorAll('.p-radiobutton').forEach(radiobutton => {
    const isChecked = radiobutton.classList.contains('p-radiobutton-checked');
    const icon = document.createElement('i');
    icon.className = `pi ${isChecked ? 'pi-check-circle' : 'pi-circle'}`;
    icon.style.marginRight = '8px';
    icon.style.color = isChecked ? '#007ad9' : '#6c757d';
    icon.style.fontSize = '14px';
    const parent = radiobutton.parentElement;
    parent?.insertBefore(icon, radiobutton);
    radiobutton.remove();
  });

  clone.querySelectorAll('textarea').forEach(textarea => {
    const textDiv = document.createElement('div');
    textDiv.textContent = textarea.value;
    Object.assign(textDiv.style, {
      border: '1px solid #ccc',
      padding: '8px',
      borderRadius: '4px',
      backgroundColor: '#f9f9f9',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap'
    });
    textarea.parentNode?.replaceChild(textDiv, textarea);
  });

  clone.querySelectorAll('img').forEach(img => {
    if (!img.src || img.src === '') {
      img.remove();
      return;
    }
    
    if (img.src.startsWith('data:') && !img.complete) {
      img.style.display = 'none';
      return;
    }
    
    Object.assign(img.style, {
      maxWidth: '100%',
      maxHeight: '300px',
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
      display: 'block',
      margin: '10px auto'
    });
  });

  clone.querySelectorAll('span, p, div, li').forEach(el => {
    Object.assign(el.style, {
      wordWrap: 'break-word',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
      overflowWrap: 'break-word'
    });
  });

  return clone;
}

async function renderFieldsetToCanvas(fieldset, contentWidthMm) {
  const originalCreatePattern = CanvasRenderingContext2D.prototype.createPattern;
  CanvasRenderingContext2D.prototype.createPattern = function(image, repetition) {
    try {
      if (image && image.tagName === 'CANVAS' && (image.width === 0 || image.height === 0)) {
        return null;
      }
      return originalCreatePattern.call(this, image, repetition);
    } catch (e) {
      return null;
    }
  };

  const tempContainer = document.createElement('div');

  try {
    Object.assign(tempContainer.style, {
      position: 'absolute',
      left: '-9999px',
      top: '0',
      background: 'white',
      padding: '10px',
      width: contentWidthMm + 'mm',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      lineHeight: '1.4'
    });
    const clone = fieldset.cloneNode(true);
    Object.assign(clone.style, {
      width: '100%',
      marginBottom: '0',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '10px'
    });
    tempContainer.appendChild(clone);
    document.body.appendChild(tempContainer);
    clone.querySelectorAll('canvas').forEach(canvas => {
      canvas.remove();
    });
    
    clone.querySelectorAll('button, .p-button').forEach(btn => {
      btn.remove();
    });

  await new Promise(resolve => {
    const images = tempContainer.querySelectorAll('img');
    if (images.length === 0) {
      resolve();
      return;
    }

    let loadedCount = 0;
    const checkComplete = () => {
      loadedCount++;
      if (loadedCount === images.length) {
        setTimeout(resolve, 100);
      }
    };

    images.forEach(img => {
      if (img.src.startsWith('data:')) {
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          checkComplete();
        } else {
          const testImg = new Image();
          testImg.onload = () => {
            img.width = testImg.width;
            img.height = testImg.height;
            checkComplete();
          };
          testImg.onerror = checkComplete;
          testImg.src = img.src;
        }
      } else if (img.complete) {
        checkComplete();
      } else {
        img.onload = checkComplete;
        img.onerror = checkComplete;
      }
    });
  });

    const rect = tempContainer.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      tempContainer.style.width = contentWidthMm + 'mm';
      tempContainer.style.minHeight = '100px';
      await new Promise(r => requestAnimationFrame(r));
    }

    const canvas = await html2canvas(tempContainer, {
      scale: 1.5,
      useCORS: true,
      allowTaint: false,
      scrollX: 0,
      scrollY: 0,
      letterRendering: true,
      logging: false,
      backgroundColor: '#ffffff',
      ignoreElements: (element) => {
        return element.tagName === 'CANVAS';
      }
    });

    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Canvas gerado tem dimensões inválidas (0x0)');
    }

    return canvas;
  } catch (error) {
    console.error('Erro ao gerar canvas:', error);
    throw error;
  } finally {
    CanvasRenderingContext2D.prototype.createPattern = originalCreatePattern;

    if (document.body.contains(tempContainer)) {
      document.body.removeChild(tempContainer);
    }
  }
}

function addCanvasToPdf(canvas, pdf, opts) {
  const {
    pdfWidth,
    pdfHeight,
    margin,
    currentYRef,
    contentHeight,
    contentWidth
  } = opts;

  const imgWidthPx = canvas.width;
  const imgHeightPx = canvas.height;
  const imgWidthMm = imgWidthPx * PX_TO_MM;
  const imgHeightMm = imgHeightPx * PX_TO_MM;
  const scale = contentWidth / imgWidthMm;
  const scaledWidthMm = contentWidth;
  const scaledHeightMm = imgHeightMm * scale;

  if (scaledHeightMm <= (pdfHeight - margin - currentYRef.value)) {
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    pdf.addImage(imgData, 'JPEG', margin, currentYRef.value, scaledWidthMm, scaledHeightMm);
    currentYRef.value += scaledHeightMm + 5;
    return;
  }

  const sliceHeightMm = contentHeight;
  const sliceHeightPx = Math.floor(sliceHeightMm / PX_TO_MM / scale);

  let yPx = 0;
  while (yPx < imgHeightPx) {
    if (currentYRef.value > margin) {
      pdf.addPage();
      currentYRef.value = margin;
    }

    const remainingPx = imgHeightPx - yPx;
    const chunkPx = Math.min(sliceHeightPx, remainingPx);

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = imgWidthPx;
    sliceCanvas.height = chunkPx;
    const ctx = sliceCanvas.getContext('2d');
    ctx.drawImage(
      canvas,
      0, yPx, imgWidthPx, chunkPx,
      0, 0, imgWidthPx, chunkPx
    );

    const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHeightMm = (chunkPx * PX_TO_MM) * scale;

    pdf.addImage(
      sliceData,
      'JPEG',
      margin,
      currentYRef.value,
      scaledWidthMm,
      sliceHeightMm
    );
    currentYRef.value += sliceHeightMm + 5;
    yPx += chunkPx;

    if (yPx < imgHeightPx) {
      pdf.addPage();
      currentYRef.value = margin;
    }
  }
}

async function exportItem() {
  if (exporting.value) return;
  exporting.value = true;

  try {
    await nextTick();
    await new Promise(r => requestAnimationFrame(r));

    const originalContent = document.getElementById('itemVisualizationDialogContent');
    if (!originalContent) throw new Error('Conteúdo não encontrado.');

    const sanitized = sanitizeContentNode(originalContent);
    const fieldsets = sanitized.querySelectorAll('fieldset');

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const contentWidth = pdfWidth - (2 * PDF_MARGIN);
    const contentHeight = pdfHeight - (2 * PDF_MARGIN);
    const currentYRef = { value: PDF_MARGIN };

    for (let i = 0; i < fieldsets.length; i++) {
      const fs = fieldsets[i];
      let canvas;
      try {
        canvas = await renderFieldsetToCanvas(fs, contentWidth);
      } catch (err) {
        console.error('Erro ao renderizar fieldset', i, err);
        continue;
      }
      addCanvasToPdf(canvas, pdf, {
        pdfWidth,
        pdfHeight,
        margin: PDF_MARGIN,
        currentYRef,
        contentHeight,
        contentWidth
      });
    }

    const now = new Date();
    const fileName = `item_${String(now.getDate()).padStart(2,'0')}_${String(now.getMonth()+1).padStart(2,'0')}_${now.getFullYear()}.pdf`;
    pdf.save(fileName);
  } catch (e) {
    console.error(e);
    ErrorUtils.handleError(e);
  } finally {
    exporting.value = false;
  }
}

watch(() => props.item, () => {
  correctAlternative.value = getCorrectAlternative();
  const f = feedbackOptions.find(option => option.label === props.item?.question.feedback);
  feedback.value = f ? String(f.value) : null;
});
</script>

<template>
  <div>
    <ConfirmPopup/>

    <Dialog id="itemVisualizationDialog"
            :visible="props.itemVisualizationDialogVisible"
            @update:visible="props.hideItemVisualization"
            modal
            style="width: 65vw; min-height: 300px">

      <template #header>
        <div class="flex direction-column">
          <h3 class="font-bold text-xl">Visualização do item</h3>
        </div>
      </template>

      <div id="itemVisualizationDialogContent" class="w-12">
        <Fieldset id="contextFieldset" legend="Contexto" class="pr-2">
          <div class="flex align-items-center">
            <div class="flex flex-grow-1 w-12">
              <span>{{ props.item.question.context }}</span>
            </div>
            <Button v-if="props.item.question.context"
                    icon="pi pi-copy"
                    class="p-button-rounded p-button-text w-1"
                    @click="copyToClipboard(props.item.question.context)"/>
          </div>
        </Fieldset>

        <Fieldset v-if="(props.item.information.supportImages || []).length > 0" legend="Imagem de suporte">
          <Card class="card-image">
            <template #content>
              <img
                :src="props.item.information.supportImages[0]?.base64 || ''"
                :alt="props.item.information.supportImages[0].description"
                class="image-preview" />
              <div class="mt-2 flex align-items-center">
                <div class="w-12 m-3">
                  <span class="mr-1 font-bold">Descrição:</span>
                  <span>{{ props.item.information.supportImages[0].description ?? 'Não informado' }}</span>
                </div>
                <Button v-if="props.item.information.supportImages[0]?.description"
                        icon="pi pi-copy"
                        class="p-button-rounded p-button-text w-1"
                        @click="copyToClipboard(props.item.information.supportImages[0]?.description)"/>
              </div>
              <Divider/>
              <div class="flex align-items-center">
                <div class="w-12 m-3">
                  <span class="text-source-image m-0 mr-1 font-bold">Fonte:</span>
                  <a v-if="StringUtils.checkIfLink(props.item.information.supportImages[0].source)"
                     :href="props.item.information.supportImages[0].source"
                     target="_blank"
                     class="text-source-image">
                    {{ props.item.information.supportImages[0].source }}
                  </a>
                  <span v-else class="text-source-image">
                    {{ props.item.information.supportImages[0].source ?? 'Não informado' }}
                  </span>
                </div>
                <Button v-if="props.item.information.supportImages[0]?.source"
                        icon="pi pi-copy"
                        class="p-button-rounded p-button-text w-1"
                        @click="copyToClipboard(props.item.information.supportImages[0]?.source)"/>
              </div>
            </template>
          </Card>
        </Fieldset>

        <Fieldset legend="Comando" class="pr-2">
          <div class="flex align-items-center">
            <div class="w-12">
              <span>{{ props.item.question.command }}</span>
            </div>
            <Button v-if="props.item.question.command"
                    icon="pi pi-copy"
                    class="p-button-rounded p-button-text w-1"
                    @click="copyToClipboard(props.item.question.command)"/>
          </div>
        </Fieldset>

        <Fieldset legend="Alternativas" class="pr-2">
          <div v-for="alternative in props.item.question.alternatives"
               :key="alternative.value"
               class="mb-4">
            <div class="flex align-items-center">
              <div class="w-12">
                <RadioButton v-model="correctAlternative"
                             :inputId="alternative.number"
                             :name="alternative.text"
                             :value="alternative.number"
                             class="mr-2"
                             readonly/>
                <span>{{ alternative.text }}</span>
              </div>
              <Button v-if="alternative.text"
                      icon="pi pi-copy"
                      class="p-button-rounded p-button-text w-1"
                      @click="copyToClipboard(alternative.text)"/>
            </div>

            <div class="flex align-items-center">
              <div class="w-12">
                <span class="mr-2 font-bold">Justificativa:</span>
                <span>{{ alternative.justification }}</span>
              </div>
              <Button v-if="alternative.justification"
                      icon="pi pi-copy"
                      class="p-button-rounded p-button-text w-1"
                      @click="copyToClipboard(alternative.justification)"/>
            </div>
          </div>
        </Fieldset>

        <Fieldset legend="Informações adicionais" class="px-4">
          <div class="grid">
            <div class="w-6 m-0 mt-2">
              <span class="font-bold mr-2">Área tecnológica:</span>
              <span>{{ props.item.information.technologicalArea?.name }}</span>
            </div>
            <div class="w-6 m-0 mt-2">
              <span class="font-bold mr-2">Versão de vigência da matriz:</span>
              <span>{{ props.item.information.course?.matrixVersion }}</span>
            </div>
            <div class="w-6 m-0 mt-3">
              <span class="font-bold mr-2">Eixo tecnológico:</span>
              <span>{{ props.item.information.technologicalArea?.technologicalAxis }}</span>
            </div>
            <div class="w-6 m-0 mt-3">
              <span class="font-bold mr-2">Versão IN:</span>
              <span>{{ props.item.information.course?.inVersion }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <span class="font-bold mr-2">Curso:</span>
              <span>{{ props.item.information.course?.name }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <span class="font-bold mr-2">Competência geral:</span>
              <span>{{ props.item.information.course?.generalCompetence }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <span class="font-bold mr-2">Função:</span>
              <span>{{ props.item.information.subfunction?.function }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <span class="font-bold mr-2">Subfunção:</span>
              <span>{{ props.item.information.subfunction?.name }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <span class="font-bold mr-2">Capacidade:</span>
              <span>{{ props.item.information.capacity?.name }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <label class="font-bold" for="taxonomyInfo">
                Níveis cognitivos avaliados (Taxonomia de Bloom):
              </label>
              <span id="capacidadeInfo">{{ props.item.information.capacity?.taxonomy }}</span>
            </div>
            <div class="w-12 m-0 mt-3">
              <span class="font-bold mr-2">Conhecimentos:</span>
              <ul>
                <li v-for="knowledge in props.item.information.knowledge"
                    :key="knowledge.idKnowledge">
                  {{ knowledge.name }}
                </li>
              </ul>
            </div>
            <div class="w-full mt-3 flex justify-content-between align-items-center">
              <div class="w-4 m-0">
                <span class="font-bold mr-2">Nível de dificuldade:</span>
                <span>{{ props.item.information.difficultyLevel?.label }}</span>
              </div>
              <div class="w-4 m-0">
                <span class="font-bold mr-2">Formato do item:</span>
                <span>{{ props.item.information.format?.label }}</span>
              </div>
              <div class="w-4 m-0">
                <span class="font-bold mr-2">Quantidade de alternativas:</span>
                <span>{{ props.item.information.numberOfAlternatives }}</span>
              </div>
            </div>
          </div>
        </Fieldset>

        <Fieldset v-if="props.item.information.additionalInstructions"
                  legend="Instruções complementares para a IA generativa"
                  class="p-2">
          <span>Instruções complementares fornecidas:</span>
          <div class="w-12 mt-2 relative">
            <Textarea v-model="props.item.information.additionalInstructions"
                      rows="5"
                      readonly
                      class="w-12"/>
            <Button v-if="props.item.information.additionalInstructions"
                    icon="pi pi-copy"
                    class="p-button-rounded p-button-text addicional-instructions-copy-button w-1"
                    @click="copyToClipboard(props.item.information.additionalInstructions)"/>
          </div>
        </Fieldset>

        <Fieldset id="feedbackFieldset" legend="Feedback" class="pr-2">
          <div class="w-12 flex justify-content-center">
            <span>Forneça seu feedback para aprimorarmos os algoritmos de IA na geração de itens de avaliação.</span>
          </div>
          <div class="flex mt-2 justify-content-center">
            <div v-for="feedbackOption in feedbackOptions"
                 :key="feedbackOption.value"
                 class="mr-2 flex-1 flex justify-content-center">
              <RadioButton v-model="feedback"
                           :inputId="String(feedbackOption.value)"
                           :name="feedbackOption.label"
                           :value="String(feedbackOption.value)"
                           :readonly="hasFeedback()"
                           @click="sendFeedback($event, feedbackOption.value)"
                           @blur="onCloseFeedback"
                           class="mr-2"/>
              <label :for="feedbackOption.value">{{ feedbackOption.label }}</label>
            </div>
          </div>
        </Fieldset>
      </div>

      <template #footer>
        <div>
          <Button id="exportItem"
                  :label="exporting ? 'Exportando...' : 'Exportar item'"
                  icon="pi pi-download"
                  class="mr-2 mt-3"
                  :disabled="exporting"
                  @click="exportItem"/>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.card-image :deep(div) {
  overflow: hidden;
  padding: 0 !important;
  justify-content: center;
  margin: 10px;
}
.image-preview {
  width: 100%;
  border-radius: 8px;
}

.p-fieldset {
  margin-top: 1rem;
}
</style>